// THE one brand table for this console, in tkt-back-office's shape (its src/lib/brand.ts): a single
// build serves every client, the brand it wears is decided ONCE here, and everything else reads
// `BRAND.<field>`, never a scattered `host.includes(…)` ternary. Add a client = add a row here, a
// colour block in styles/brands.css and a chart set in ui/charts/palette.js.
//
// Shared fields keep tkt-back-office's names and meaning (key, name, product, logo, wordmark, font),
// and MoMo keeps its key there, `moments`. What is ours, and why:
//
//   domains     EXACT domains, not substrings. ops.momoticketing.com matches momoticketing.com
//               because it ends in ".momoticketing.com"; notmomoticketing.com matches nothing.
//               tkt-back-office's `includes('momo')` has to order its tests to dodge collisions
//               (its own comments on "party"); an exact match has nothing to dodge.
//   colours     not a field: this console's design is coloured (MoMo blue sidebar, yellow main
//               action), so one `accent` cannot re-skin it. Each brand is a block of CSS variables in
//               styles/brands.css under <html data-brand="<key>">.
//   team, seller, emailExample, signInNote, sponsor, staffEmailDomain
//               the words the screens use for the client: who runs it ("the MTN team"), what it calls
//               the businesses selling tickets, the sign-in placeholder and note, its comp sponsor,
//               and the work domain its staff are invited at (Users), or null for any address.
//   storefront  the buyer site's origin from a local host, and an event's path on it.
//
// Which brand: `?brand=<key>` (kept in localStorage `brandOverride`, `?brand=clear` removes it), as in
// tkt-back-office; then the host's domain; then, on a host no brand claims (localhost, previews),
// VITE_BRAND, else MoMo. MoMo, not TKT: this console has no TKT brand, and MoMo is its first client.
//
// The backend has its own brand identity (tkt-backend utils/brandProfile.ts over commPolicy's
// comm-brands): sign-in codes, invites and senders. A client with its own tenant needs a row there too.
// The scanner app per domain lives with the other cross-host URLs, in lib/backendUrl.js.
import momoLogo from '../assets/images/momo-mark.png';
import tktLogo from '../assets/images/tkt-mark.png';

export const BRANDS = {
  // MoMo, MTN's. Key `moments`, as tkt-back-office and the backend tenant have it.
  moments: {
    key: 'moments',
    name: 'MoMo',
    product: 'MoMo Ticketing',
    logo: momoLogo, // the square mark: sidebar, sign-in and the tab icon
    font: "'MTN Brighter Sans'", // local @font-face in styles/brands.css
    domains: ['momoticketing.com', 'moments.ug'],
    team: 'MTN',
    signInNote: 'MTN Uganda · internal use only',
    emailExample: 'name@mtn.com',
    // Staff are invited at their work address only (Users). Null: any address.
    staffEmailDomain: 'mtn.com',
    // Help & support in the user menu (the inspo's contact, confirmed 2026-10-29).
    support: { email: 'ticketing.support@mtn.com', phone: '0312 120 000' },
    sponsor: 'MTN Uganda',
    seller: { one: 'merchant', many: 'merchants' },
    storefront: { origin: 'https://momoticketing.com', eventPath: (slug) => `/events/${slug}` },
  },
  // Aki Bua. Built as an instance with a backend of its own (LAND=akii-bua; tkt-backend commPolicy's
  // `akii-bua` row), until it is decided whether it sits on TKT's (2026-10-29). It has no domain, logo
  // or mailbox yet, so it wears TKT's logo and sends from TKT's; it says "organizer", as TKT does.
  // Run locally with ?brand=akii-bua; nothing is served for it until it has a domain.
  'akii-bua': {
    key: 'akii-bua',
    name: 'Akii Bua',
    product: 'Akii Bua',
    logo: tktLogo, // TKT's until Akii Bua has its own
    font: "'Plus Jakarta Sans Variable', 'Plus Jakarta Sans'", // @fontsource-variable, imported in main.jsx
    domains: [],
    team: 'Akii Bua',
    signInNote: 'Akii Bua · internal use only',
    emailExample: 'name@company.com',
    staffEmailDomain: null,
    // TKT's support until Akii Bua has its own (tkt-back-office lib/brand.ts: TKT's supportEmail).
    support: { email: 'support@tkt.ug', phone: null },
    sponsor: null,
    seller: { one: 'organizer', many: 'organizers' },
    storefront: { origin: 'https://book.tkt.ug', eventPath: (slug) => `/${slug}` },
  },
};

export const ALL_BRANDS = Object.values(BRANDS);

const isLocalOrPreviewHost = (h) => !h || h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || /^(\d{1,3}\.){3}\d{1,3}$/.test(h) || h.endsWith('.local') || h.endsWith('.workers.dev');

/** The brand for a host (pure, for tests): `override` first, then the domain, then `fallback`, else MoMo. */
export function brandFor(hostname, { override, fallback } = {}) {
  if (override && BRANDS[override]) return BRANDS[override];
  const h = String(hostname || '').toLowerCase().split(':')[0];
  if (!isLocalOrPreviewHost(h)) {
    const hit = ALL_BRANDS.find((b) => b.domains.some((d) => h === d || h.endsWith(`.${d}`)));
    if (hit) return hit;
  }
  return BRANDS[fallback] || BRANDS.moments;
}

/** `?brand=` and its remembered override, as tkt-back-office reads them. */
function readOverride() {
  if (typeof window === 'undefined') return null;
  try {
    const q = new URLSearchParams(window.location.search).get('brand');
    if (q === 'clear') window.localStorage.removeItem('brandOverride');
    else if (q && BRANDS[q]) window.localStorage.setItem('brandOverride', q);
    return window.localStorage.getItem('brandOverride');
  } catch {
    return null; // storage unavailable: the host decides
  }
}

export const detectBrand = (host) =>
  brandFor(host ?? (typeof window !== 'undefined' ? window.location?.hostname : ''), { override: readOverride(), fallback: import.meta.env?.VITE_BRAND });

export const BRAND = detectBrand();

const capital = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
/**
 * What this client calls the businesses selling tickets, in the four forms copy needs: "merchant",
 * "merchants", "Merchant", "Merchants" for MoMo; "organizer"… for Aki Bua and TKT's other clients.
 * Screens say `${SELLER.One} onboarding`, never "Merchant onboarding".
 */
export const SELLER = { one: BRAND.seller.one, many: BRAND.seller.many, One: capital(BRAND.seller.one), Many: capital(BRAND.seller.many) };

/** A tab icon for a brand without its logo yet: its initials on its accent, never another client's mark. */
function initialsIcon(brand) {
  const css = getComputedStyle(document.documentElement);
  const rgb = (v) => `rgb(${css.getPropertyValue(v).trim().split(/\s+/).join(',')})`;
  const initials = brand.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="7" fill="${rgb('--accent')}"/><text x="16" y="21" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="800" fill="${rgb('--nav-bg')}">${initials}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** Put the brand on the page, before the first paint: its colours (data-brand), type face, tab icon and title. */
export function applyBrand(brand = BRAND) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.brand = brand.key;
  root.style.setProperty('--font-sans', brand.font);
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.removeAttribute('type');
  link.href = brand.logo || initialsIcon(brand);
  // The title is the brand's alone while no route sets its own (tkt-back-office's router does, in lib/pageTitle).
  document.title = `${brand.product} · Back Office`;
}
